import React, { useCallback, useMemo, FC } from "react";
import { MdsTabBarItem } from "@/design-system/components/tab-bar/types";
import { EmotionClassStyles } from "@/domains/emotion/types";
import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import styled from "@emotion/styled";
import { MdsIcon } from "@/design-system/components/icon/MdsIcon";
import { MdsIconKind } from "@/design-system/components/icon";

export interface MdsTabBarItemContentProps extends EmotionClassStyles {
  selectedItemId: string;
  item: MdsTabBarItem;
}

const StyledWrapper = styled.button<{ $isSelected: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  background-color: ${({ theme, $isSelected }) =>
    $isSelected
      ? theme.colors.background.menu.selected
      : theme.colors.background.button.transparent.default};
  transition: background-color 0.08s ease;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[5]}`};

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.button.transparent.hover};
  }
  &:active {
    background-color: ${({ theme }) => theme.colors.background.button.transparent.hover};
  }

  @container mds-panel (max-width: 750px) {
    display: flex;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius.xlarge};
    background-color: ${({ theme, $isSelected }) =>
      $isSelected ? theme.colors.background.button.default.default : undefined};
    padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[5]}`};
    ${({ $isSelected }) => !$isSelected && `display: none;`}
  }
`;

const StyledLabel = styled(MDSText)`
  user-select: none;
  text-align: start;

  @container mds-panel (max-width: 750px) {
    font-weight: ${({ theme }) => theme.fontWeights.smedium};
    line-height: ${({ theme }) => theme.lineHeights.xsmall};
  }
`;

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsTabBarItemContent: FC<MdsTabBarItemContentProps> = ({
  item,
  selectedItemId,
  className,
}) => {
  const { onClick: onItemClick, id: itemId } = item;

  const handleClick = useCallback(() => onItemClick({ itemId }), [onItemClick, itemId]);
  const isSelected = useMemo(() => item.id === selectedItemId, [item.id, selectedItemId]);

  return (
    <StyledWrapper $isSelected={isSelected} onClick={handleClick} className={className}>
      <StyledLabel variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Primary}>
        {item.label}
      </StyledLabel>
      <StyledIcon kind={MdsIconKind.AngleDown} $isSelected={isSelected} />
    </StyledWrapper>
  );
};

const StyledIcon = styled(MdsIcon)<{ $isSelected: boolean }>(({ theme, $isSelected }) => ({
  display: "none",
  color: theme.colors.icon.primary,

  "@container mds-panel (max-width: 750px)": {
    display: $isSelected ? "block" : undefined,
  },
}));
